import { useEffect, useRef } from "react";
import { canManageReport } from "@/profitability/access";
import { useTemplateChatSession } from "@/profitability/template-chat/use-template-chat";
import type { ProfitabilityReport } from "@/profitability/types";
import { useAgentNav } from "./use-agent-nav";
import type { usePageEdge } from "./use-page-edge";

/**
 * Edit agent on an agent's page: who may use it (changing the agent is
 * finance's, Profitability::Access), whether it is open, and its
 * conversation, read while it is open, while the agent is being set up, or
 * while a draft of changes waits. One aside at a time: opening Edit agent
 * closes Ask Volca, and the other way round.
 */
export const useEditAgent = (
  businessId: string,
  reportId: string,
  report: ProfitabilityReport | undefined,
  edge: ReturnType<typeof usePageEdge>,
  /** The period's run on screen, once the agent runs. */
  runId: string | null,
) => {
  const manages = !!report && canManageReport(report) && !report.archived_at;
  // Being set up: the page is the builder, not periods.
  const settingUp = manages && !report.published;
  const nav = useAgentNav(settingUp);
  const editing = manages && nav.editing;
  const session = useTemplateChatSession(
    businessId,
    reportId,
    report?.build_status === "building",
    manages && (editing || settingUp || !!report.draft_pending),
    // What the reply reads: the sample week while the agent is being built,
    // else the period on screen.
    settingUp ? (report.draft_sample_run_id ?? null) : runId,
  );
  // Someone who may not change the agent opened a link with Edit agent open
  // (one finance shared): the address forgets it.
  const stray = !!report && !manages && nav.explicit;
  useEffect(() => {
    if (stray) void nav.closeEdit();
  }, [stray, nav]);
  const asking = !!edge.chat;
  const wasEditing = useRef(editing);
  const wasAsking = useRef(asking);
  useEffect(() => {
    if (editing && !wasEditing.current && asking) edge.closeChat();
    else if (asking && !wasAsking.current && editing) void nav.closeEdit();
    wasEditing.current = editing;
    wasAsking.current = asking;
  }, [editing, asking, edge, nav]);
  return { manages, settingUp, editing, nav, session };
};
